import { Icon, LinkButton, MuiAlert, Paper, Typography, strIsNullOrEmpty } from '@fox/atoms';
import { FlexColumnSpaced, FlexRowSpaced } from '@fox/layout';
import { useEffect } from 'react';
import styled from 'styled-components';
import { SwiftFoxEvent, SwiftFoxRsvpOption } from './contracts';
import { RsvpButton } from './rsvp-button';
import { formatEventDate } from './util';
import { DEFAULT_RSVP_ID } from '../event-list/util';

const ButtonWrapper = styled.div`
	flex-direction: row;
	align-items: center;
	display: flex;
	@media (max-width: 600px) {
		flex-direction: column;
		align-items: flex-start;
	}
`;

const EventWrapper = styled.div`
	flex-direction: column;
	width: 100%;
	display: flex;
	align-items: space-between;
`;

const EventCardPaperWrapper = styled.div`
	margin: 1rem;
	@media (max-width: 600px) {
		margin: 0rem;
	}
`;

const DescriptionWrapper = styled.div`
	.eventDescription {
		@media (max-width: 600px) {
			-webkit-line-clamp: 3;
			overflow: hidden;
			display: -webkit-box;
			-webkit-box-orient: vertical;
		}
	}
`;

export function EventCard({
	event,
	refetch,
	rsvpOptions,
	isMobile
}: {
	event: SwiftFoxEvent;
	refetch: () => void;
	rsvpOptions: SwiftFoxRsvpOption[];
	isMobile: boolean;
}) {
	useEffect(() => {
		if (event.location?.lng && event.location?.lat) {
			const map = new google.maps.Map(document.getElementById(`map_${event.id}`), {
				center: { lat: event.location.lat, lng: event.location.lng },
				zoom: 14,
				disableDefaultUI: true,
				draggable: false,
				zoomControl: false,
				clickableIcons: false
			});

			const marker = new google.maps.Marker({
				position: { lat: event.location.lat, lng: event.location.lng },
				map: map,
				title: event.name
			});
		}
	}, []);

	return (
		<EventCardPaperWrapper>
			<Paper style={{ padding: '1rem' }}>
				<EventWrapper>
					<FlexColumnSpaced>
						<Typography variant="h4">{event.name}</Typography>
						<FlexRowSpaced style={{ alignItems: 'center' }}>
							<Icon iconName="CalendarToday" />
							<Typography>{formatEventDate(event.eventStart, event.eventEnd)}</Typography>
						</FlexRowSpaced>
						{!strIsNullOrEmpty(event.location?.formatted) ? (
							<FlexRowSpaced style={{ alignItems: 'center' }}>
								<Icon iconName="LocationOn" />
								{event.location?.formatted}
							</FlexRowSpaced>
						) : null}
						{!strIsNullOrEmpty(event.meetingUrl) && event?.rsvpStatus === DEFAULT_RSVP_ID.attending ? (
							<FlexRowSpaced style={{ alignItems: 'center' }}>
								<Icon iconName="Link" />
								<LinkButton
									sx={{ fontSize: 'initial', lineHeight: 'initial' }}
									component="a"
									target="_blank"
									href={event.meetingUrl}
								>
									Meeting link
								</LinkButton>
							</FlexRowSpaced>
						) : null}
						{!strIsNullOrEmpty(event.zoomId) && event?.rsvpStatus === DEFAULT_RSVP_ID.attending ? (
							<FlexRowSpaced style={{ alignItems: 'center' }}>
								<Icon iconName="Website" />
								<LinkButton
									sx={{ fontSize: 'initial', lineHeight: 'initial' }}
									component="a"
									target="_blank"
									href={`https://zoom.us/webinar/${event?.zoomId}`}
								>
									Go to zoom webinar
								</LinkButton>
							</FlexRowSpaced>
						) : null}
					</FlexColumnSpaced>
					<FlexColumnSpaced style={{ justifyContent: 'end', flexGrow: 1, marginTop: '1rem' }}>
						<DescriptionWrapper>
							<Typography
								className="eventDescription"
								style={{
									opacity: 0.6,
									marginBottom: '1rem'
								}}
							>
								{event.description}
							</Typography>
						</DescriptionWrapper>
						{event.location?.lat && event.location?.lng ? <div id={`map_${event.id}`} style={{ height: '20rem' }}></div> : null}
						<ButtonWrapper>
							<RsvpButton
								eventStart={event.eventStart}
								eventId={event.id}
								refetch={refetch}
								isMobile={isMobile}
								rsvpFormLink={event.rsvpFormLink}
								availableTickets={event.availableTickets}
							/>
							{!strIsNullOrEmpty(event.rsvpStatus) ? (
								<MuiAlert severity="success" sx={{ backgroundColor: 'transparent' }}>
									{rsvpOptions?.find(option => option.id === event?.rsvpStatus)?.displayName}
								</MuiAlert>
							) : null}
						</ButtonWrapper>
					</FlexColumnSpaced>
				</EventWrapper>
			</Paper>
		</EventCardPaperWrapper>
	);
}
